import React, { useState, useEffect } from "react";
import api from "../api";
import "./estilodash.css";

const AdicionaPagamentos = () => {
  const [clientes, setClientes] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [pagamentos, setPagamentos] = useState([]);

  const [clienteId, setClienteId] = useState("");
  const [mesReferencia, setMesReferencia] = useState("");
  const [dataPagamento, setDataPagamento] = useState("");
  const [valor, setValor] = useState("");
  const [metodoPagamento, setMetodoPagamento] = useState("PIX");
  const [status, setStatus] = useState("Aguardando pagamento");
  const [descricao, setDescricao] = useState("");

  const [pagamentoIdEdicao, setPagamentoIdEdicao] = useState(null);

  const [carregando, setCarregando] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    carregarDadosIniciais();
  }, []);

  useEffect(() => {
    // sempre que mudar cliente ou mês, verifica se já existe pagamento
    if (clienteId && mesReferencia) {
      prepararPagamentoParaClienteMes();
    } else {
      // limpamos modo edição se tirar cliente/mês
      setPagamentoIdEdicao(null);
      setValor("");
      setDescricao("");
      setStatus("Aguardando pagamento");
    }
  }, [clienteId, mesReferencia, pagamentos]);

  // =====================
  // CARREGAR DADOS INICIAIS
  // =====================

  const carregarDadosIniciais = async () => {
    try {
      setCarregando(true);
      setMsg("");

      const [respCli, respPag, respPlanos] = await Promise.all([
        api.get("/api/clientes"),
        api.get("/api/pagamentos"),
        api.get("/api/planos-ativos"),
      ]);

      setClientes(respCli.data || []);
      setPagamentos(respPag.data || []);
      setPlanos(respPlanos.data || []);

      // data hoje default
      const hoje = new Date();
      const yyyy = hoje.getFullYear();
      const mm = String(hoje.getMonth() + 1).padStart(2, "0");
      const dd = String(hoje.getDate()).padStart(2, "0");
      setDataPagamento(`${yyyy}-${mm}-${dd}`);
    } catch (err) {
      console.error("Erro ao carregar dados iniciais:", err);
      setMsg("Erro ao carregar dados. Verifique login/servidor.");
    } finally {
      setCarregando(false);
    }
  };

  // =====================
  // HELPERS
  // =====================
  const statusQuitado = (st) => {
    const s = String(st || "")
      .trim()
      .toLowerCase();
    return s === "pago" || s === "isento" || s === "cancelado";
  };

  const getCliente = (id) => clientes.find((c) => Number(c.id) === Number(id));

  const getPlano = (id) => planos.find((p) => Number(p.id) === Number(id));

  const findPagamentoClienteMes = (clienteId, mesRef) => {
    return pagamentos.find(
      (p) =>
        Number(p.cliente_id) === Number(clienteId) &&
        p.mes_referencia === mesRef
    );
  };

  // Calcula uma data de vencimento sugerida com base em mes_referencia + dia_vencimento do cliente
  const calcularDataVencimento = (cliente, mesRef) => {
    if (!cliente || !mesRef) return null;
    const dia = cliente.dia_vencimento;
    if (!dia) return null;

    const [anoStr, mesStr] = mesRef.split("-");
    const ano = parseInt(anoStr, 10);
    const mes = parseInt(mesStr, 10);
    if (isNaN(ano) || isNaN(mes)) return null;

    let diaNum = parseInt(dia, 10);
    if (isNaN(diaNum)) return null;

    // garante 1–28
    if (diaNum < 1) diaNum = 1;
    if (diaNum > 28) diaNum = 28;

    const dd = String(diaNum).padStart(2, "0");
    const mm = String(mes).padStart(2, "0");

    return `${ano}-${mm}-${dd}`;
  };

  // =====================
  // PREPARAR FORM AO MUDAR CLIENTE/MÊS
  // =====================

  const prepararPagamentoParaClienteMes = () => {
    const pagExistente = findPagamentoClienteMes(clienteId, mesReferencia);
    const cliente = getCliente(clienteId);

    if (pagExistente) {
      // modo edição
      setPagamentoIdEdicao(pagExistente.id);
      setDataPagamento(
        pagExistente.data_pagamento
          ? pagExistente.data_pagamento.slice(0, 10)
          : dataPagamento || ""
      );
      setValor(pagExistente.valor != null ? String(pagExistente.valor) : "");
      setMetodoPagamento(pagExistente.metodo_pagamento || "PIX");
      setStatus(pagExistente.status || "Aguardando pagamento");
      setDescricao(pagExistente.descricao || "");
    } else {
      // novo pagamento
      setPagamentoIdEdicao(null);

      // se tiver cliente + mês e dia de vencimento, já sugere a data de vencimento no campo
      const dataSug = calcularDataVencimento(cliente, mesReferencia);
      if (dataSug) {
        setDataPagamento(dataSug);
      } else {
        // ✅ MELHORIA: sempre carregar com a data de hoje (não depende de estar vazio)
        const hoje = new Date();
        const yyyy = hoje.getFullYear();
        const mm = String(hoje.getMonth() + 1).padStart(2, "0");
        const dd = String(hoje.getDate()).padStart(2, "0");
        setDataPagamento(`${yyyy}-${mm}-${dd}`);
      }

      setValor("");
      setMetodoPagamento("PIX");
      setStatus("Aguardando pagamento");
      setDescricao("");
    }
  };

  const handleUsarSugestao = (valorSugestao) => {
    if (!valorSugestao || valorSugestao <= 0) {
      alert("Sugestão de valor é zero. Verifique plano, slots ou isenção.");
      return;
    }
    setValor(String(valorSugestao.toFixed(2)));
  };

  const handleZerarValor = () => {
    setValor("0.00");
  };

  const handleLimpar = () => {
    setClienteId("");
    setMesReferencia("");

    // ✅ MELHORIA: ao limpar, manter data como HOJE
    const hoje = new Date();
    const yyyy = hoje.getFullYear();
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const dd = String(hoje.getDate()).padStart(2, "0");
    setDataPagamento(`${yyyy}-${mm}-${dd}`);

    setValor("");
    setMetodoPagamento("PIX");
    setStatus("Aguardando pagamento");
    setDescricao("");
    setPagamentoIdEdicao(null);
    setMsg("");
  };

  // =====================
  // SALVAR (POST/PUT) MANUAL
  // =====================

  const handleSalvarPagamento = async (e) => {
    e.preventDefault();

    if (!clienteId) {
      alert("Selecione um cliente.");
      return;
    }
    if (!mesReferencia) {
      alert("Informe o mês de referência (YYYY-MM).");
      return;
    }
    if (!dataPagamento) {
      alert("Informe a data do pagamento (ou previsão).");
      return;
    }

    // ✅ BLOQUEIO REAL: se já existe e está quitado, não permite sobrescrever (nem PUT nem POST)
    const existente = findPagamentoClienteMes(clienteId, mesReferencia);
    if (existente && statusQuitado(existente.status)) {
      alert(
        `Este mês (${mesReferencia}) já possui pagamento com status "${existente.status}" (ID ${existente.id}). ` +
          `Não é permitido sobrescrever/alterar.`
      );
      return;
    }

    try {
      setCarregando(true);

      const body = {
        cliente_id: clienteId,
        data_pagamento: dataPagamento,
        valor: valor ? Number(valor) : 0,
        descricao,
        mes_referencia: mesReferencia,
        metodo_pagamento: metodoPagamento,
        status,
      };

      if (pagamentoIdEdicao) {
        await api.put(`/api/pagamentos/${pagamentoIdEdicao}`, body);
        setMsg("Pagamento atualizado com sucesso!");
      } else {
        await api.post("/api/pagamentos", body);
        setMsg("Pagamento cadastrado com sucesso!");
      }

      const respPag = await api.get("/api/pagamentos");
      setPagamentos(respPag.data || []);
      prepararPagamentoParaClienteMes();
    } catch (err) {
      console.error("Erro ao salvar pagamento:", err);
      alert("Erro ao salvar pagamento.");
    } finally {
      setCarregando(false);
    }
  };

  // =====================
  // GERAR COBRANÇA DO MÊS (TODOS)
  // =====================

  const handleGerarCobrancaMes = async () => {
    let mesRef = mesReferencia;

    if (!mesRef) {
      const entrada = window.prompt(
        "Digite o mês no formato YYYY-MM (ou deixe vazio para usar o mês atual):",
        ""
      );
      if (entrada) {
        mesRef = entrada;
      }
    }

    try {
      setCarregando(true);
      const body = mesRef ? { mes_referencia: mesRef } : {};

      const resp = await api.post("/api/faturar-mes", body);

      alert(
        `Faturamento gerado para ${
          resp.data.mes_referencia
        }. Clientes processados: ${resp.data.sucesso?.length || 0}`
      );

      const respPag = await api.get("/api/pagamentos");
      setPagamentos(respPag.data || []);
    } catch (err) {
      console.error("Erro ao gerar cobrança do mês:", err);
      alert("Erro ao gerar cobrança do mês.");
    } finally {
      setCarregando(false);
    }
  };

  // =====================
  // GERAR COBRANÇA PARA UM ÚNICO CLIENTE (USANDO DIA_VENCIMENTO)
  // =====================

  const handleGerarCobrancaCliente = async () => {
    if (!clienteId) {
      alert("Selecione um cliente.");
      return;
    }
    if (!mesReferencia) {
      alert("Informe o mês de referência (YYYY-MM).");
      return;
    }

    const cliente = getCliente(clienteId);
    const plano = cliente ? getPlano(cliente.plano_id) : null;

    if (!cliente) {
      alert("Cliente não encontrado.");
      return;
    }
    if (!plano) {
      alert("Cliente sem plano ativo ou plano não encontrado.");
      return;
    }

    const ativo = String(cliente.status) === "1";
    const isentoCli = String(cliente.isento) === "1";

    const slotsIncluidos = Number(plano.slots_inclusos || 0);
    const slotsContratados = Number(cliente.slots_contratados || 0);

    let extras = 0;
    let valorCalc = 0;

    if (!ativo || isentoCli) {
      extras = 0;
      valorCalc = 0;
    } else {
      extras = Math.max(slotsContratados - slotsIncluidos, 0);
      const base = Number(plano.valor_base || 0);
      const extraSlot = Number(plano.valor_slot_extra || 0);
      valorCalc = base + extras * extraSlot;
    }

    const dataSugVenc = calcularDataVencimento(cliente, mesReferencia);

    // se não tiver dia_vencimento, usa a data de hoje como fallback
    let dataPg = dataSugVenc;
    if (!dataPg) {
      const hoje = new Date();
      const yyyy = hoje.getFullYear();
      const mm = String(hoje.getMonth() + 1).padStart(2, "0");
      const dd = String(hoje.getDate()).padStart(2, "0");
      dataPg = `${yyyy}-${mm}-${dd}`;
    }

    const body = {
      cliente_id: cliente.id,
      data_pagamento: dataPg,
      valor: valorCalc,
      descricao: `Cobrança manual ${mesReferencia}`,
      mes_referencia: mesReferencia,
      metodo_pagamento: metodoPagamento || "PIX",
      status: isentoCli ? "Isento" : "Aguardando pagamento",
    };

    try {
      setCarregando(true);

      const existente = findPagamentoClienteMes(cliente.id, mesReferencia);
      if (existente) {
        const confirma = window.confirm(
          `Já existe um pagamento para este cliente e mês (ID ${existente.id}). Deseja ATUALIZAR esse registro com os novos dados?`
        );
        if (!confirma) {
          setCarregando(false);
          return;
        }
        await api.put(`/api/pagamentos/${existente.id}`, body);
        setMsg("Faturamento do cliente atualizado com sucesso!");
      } else {
        await api.post("/api/pagamentos", body);
        setMsg("Faturamento do cliente gerado com sucesso!");
      }

      const respPag = await api.get("/api/pagamentos");
      setPagamentos(respPag.data || []);

      // atualiza o formulário local com os dados gerados
      setDataPagamento(dataPg);
      setValor(String(valorCalc.toFixed(2)));
      setMetodoPagamento(body.metodo_pagamento);
      setStatus(body.status);
      setDescricao(body.descricao);

      prepararPagamentoParaClienteMes();
    } catch (err) {
      console.error("Erro ao gerar faturamento do cliente:", err);
      alert("Erro ao gerar faturamento do cliente.");
    } finally {
      setCarregando(false);
    }
  };

  // =====================
  // CÁLCULO DA SUGESTÃO (igual listagem)
  // =====================

  const clienteSelecionado = clienteId ? getCliente(clienteId) : null;
  const planoCliente = clienteSelecionado
    ? getPlano(clienteSelecionado.plano_id)
    : null;

  let slotsIncluidosCalc = 0;
  let slotsCobradosCalc = 0;
  let extrasCalc = 0;
  let valorCalculadoSugestao = 0;
  let dataVencimentoSug = null;

  if (clienteSelecionado && planoCliente) {
    const ativo = String(clienteSelecionado.status) === "1";
    const isentoCli = String(clienteSelecionado.isento) === "1";

    slotsIncluidosCalc = Number(planoCliente.slots_inclusos || 0);
    slotsCobradosCalc = Number(clienteSelecionado.slots_contratados || 0);

    if (!ativo || isentoCli) {
      extrasCalc = 0;
      valorCalculadoSugestao = 0;
    } else {
      extrasCalc = Math.max(slotsCobradosCalc - slotsIncluidosCalc, 0);
      const base = Number(planoCliente.valor_base || 0);
      const extraSlot = Number(planoCliente.valor_slot_extra || 0);

      valorCalculadoSugestao = base + extrasCalc * extraSlot;
    }

    if (mesReferencia) {
      dataVencimentoSug = calcularDataVencimento(
        clienteSelecionado,
        mesReferencia
      );
    }
  }

  // =====================
  // RENDER
  // =====================

  return (
    <div className="pagamentos-container">
      <h3>Cadastrar / Editar Pagamentos</h3>

      <div className="barra-acoes-pagamento">
        <button type="button" onClick={handleGerarCobrancaMes}>
          Gerar faturamento do mês (todos)
        </button>
        <button type="button" onClick={handleGerarCobrancaCliente}>
          Gerar faturamento deste cliente
        </button>
        <button type="button" onClick={carregarDadosIniciais}>
          Recarregar dados
        </button>
        <button type="button" onClick={handleLimpar}>
          Limpar formulário
        </button>
      </div>

      {msg && <p style={{ color: "green", marginTop: 8 }}>{msg}</p>}
      {carregando && <p>Processando...</p>}

      <form className="form-pagamento" onSubmit={handleSalvarPagamento}>
        <div className="form-linha">
          <label>
            Cliente:
            <select
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
            >
              <option value="">Selecione...</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome} ({c.email})
                </option>
              ))}
            </select>
          </label>

          <label>
            Mês de referência:
            <input
              type="month"
              value={mesReferencia}
              onChange={(e) => setMesReferencia(e.target.value)}
            />
          </label>
        </div>

        {clienteSelecionado && (
          <div className="card-resumo-cliente">
            <h4>{clienteSelecionado.nome}</h4>
            <div className="texto-menor">{clienteSelecionado.email}</div>
            {clienteSelecionado.phone && (
              <div className="texto-menor">Tel: {clienteSelecionado.phone}</div>
            )}
            {clienteSelecionado.cpf && (
              <div className="texto-menor">CPF: {clienteSelecionado.cpf}</div>
            )}

            <div>
              <strong>Plano: </strong>
              {planoCliente ? planoCliente.nome : "—"}
              {planoCliente && (
                <span className="texto-menor">
                  {" "}
                  | Slots incluídos: {planoCliente.slots_inclusos}
                </span>
              )}
            </div>

            <div>
              <strong>Slots contratados: </strong>
              {clienteSelecionado.slots_contratados}
            </div>

            <div>
              <strong>Status cliente: </strong>
              <span
                className={
                  String(clienteSelecionado.status) === "1"
                    ? "badge badge-ativo"
                    : "badge badge-inativo"
                }
              >
                {String(clienteSelecionado.status) === "1"
                  ? "Ativo"
                  : "Inativo"}
              </span>
            </div>

            <div>
              <strong>Isento: </strong>
              <span
                className={
                  String(clienteSelecionado.isento) === "1"
                    ? "badge badge-isento"
                    : "badge badge-normal"
                }
              >
                {String(clienteSelecionado.isento) === "1"
                  ? "Sim (Isento)"
                  : "Não"}
              </span>
            </div>

            {clienteSelecionado.dia_vencimento && (
              <div>
                <strong>Dia de vencimento do cliente: </strong>
                Dia {clienteSelecionado.dia_vencimento}
                {dataVencimentoSug && (
                  <span className="texto-menor">
                    {" "}
                    | Vencimento sugerido para {mesReferencia}:{" "}
                    {dataVencimentoSug}
                  </span>
                )}
              </div>
            )}

            <div style={{ marginTop: 6 }}>
              <strong>Cálculo do plano:</strong>
              <div className="texto-menor">
                Slots incluídos: {slotsIncluidosCalc} | Slots cobrados:{" "}
                {slotsCobradosCalc} | Extras: {extrasCalc}
              </div>
              <div>
                <strong>Valor calculado (sugestão): </strong>
                R$ {valorCalculadoSugestao.toFixed(2)}
                <button
                  type="button"
                  style={{ marginLeft: 8 }}
                  onClick={() => handleUsarSugestao(valorCalculadoSugestao)}
                >
                  Usar sugestão
                </button>
                <button
                  type="button"
                  style={{ marginLeft: 8 }}
                  onClick={handleZerarValor}
                >
                  Zerar valor
                </button>
                {pagamentoIdEdicao && (
                  <span className="texto-menor" style={{ marginLeft: 8 }}>
                    (Editando pagamento ID: {pagamentoIdEdicao})
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="form-linha">
          <label>
            Data do pagamento:
            <input
              type="date"
              value={dataPagamento}
              onChange={(e) => setDataPagamento(e.target.value)}
            />
          </label>

          <label>
            Valor:
            <input
              type="number"
              step="0.01"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
            />
          </label>
        </div>

        <div className="form-linha">
          <label>
            Método de pagamento:
            <select
              value={metodoPagamento}
              onChange={(e) => setMetodoPagamento(e.target.value)}
            >
              <option value="PIX">PIX</option>
              <option value="Boleto">Boleto</option>
              <option value="Cartão">Cartão</option>
              <option value="Dinheiro">Dinheiro</option>
              <option value="Isenção">Isenção</option>
              <option value="Outro">Outro</option>
            </select>
          </label>

          <label>
            Status:
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="Aguardando pagamento">Aguardando pagamento</option>
              <option value="Pago">Pago</option>
              <option value="Isento">Isento</option>
              <option value="Cancelado">Cancelado</option>
            </select>
          </label>
        </div>

        <div className="form-linha">
          <label style={{ flex: 1 }}>
            Descrição:
            <input
              type="text"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex: Pagamento referente a..."
            />
          </label>
        </div>

        <div className="form-linha" style={{ marginTop: 10 }}>
          <button type="submit">
            {pagamentoIdEdicao ? "Salvar alterações" : "Cadastrar pagamento"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdicionaPagamentos;
